// The face graph, read into a plan once per change — the only reader on this path.
// Notes: ~/comments/aos-threejs-poc/useRenderPlan_identity_memo_and_block_detector.md

import { useCallback, useEffect, useRef } from 'react'
import { useFrame } from '@react-three/fiber'
import { faceGraphState } from '../../lib/faceGraphState.js'
import { createRenderBlockDetector } from '../../ogs/graph/rendererGate.js'
import { resolveRenderPlan, describeRenderPlan, noCompositePlan } from '../../ogs/pipeline/renderPlan.ts'
import { publishDebug, retractDebug } from '../../ogs/pipeline/ogsDebug.ts'

/** A boot that can never become ready is a diagnostic, not an eternal spinner (ENG-991). */
export const RENDER_BLOCK_DELAY_MS = 10_000

function publish(plan) {
  publishDebug('__OGS_RENDER_PLAN__', {
    plan, summary: describeRenderPlan(plan), duplicateRegions: plan.duplicateRegions,
  })
}

/**
 * Watches `faceGraphState` identity per frame and reports plan changes.
 *
 * @param onPlan     (next, prev) => void, called on the first plan and every change
 * @param onBlocked  (message|null) => void for the App-level diagnostic
 * @param started    () => boolean; after the pipeline starts, an unwired chain is an edit
 */
export function useRenderPlan({ onPlan, onBlocked, started }) {
  const nodesRef = useRef(null)
  const edgesRef = useRef(null)
  const planRef = useRef(null)
  const detector = useRef(null)
  if (!detector.current) detector.current = createRenderBlockDetector({ delayMs: RENDER_BLOCK_DELAY_MS })

  const message = useCallback((plan) => (
    plan.phase === 'created'
      ? 'The Face Blueprint has an OGS Avatar node, but its rig input is not wired — nothing can render '
        + 'this splat character. Wire Final Pose into the OGS Avatar node in the Face Blueprint.'
      : 'The Face Blueprint has no OGS Avatar node — nothing can render this splat character. Its '
        + 'blueprint was likely saved empty or overwritten; restore an earlier version, or add the '
        + 'OGS Avatar node in the Face Blueprint.'
  ), [])

  // Polled on a plain interval, NOT useFrame: a stuck boot must be detected even
  // when the render loop is idle.
  useEffect(() => {
    const poll = setInterval(() => {
      if (started()) return
      if (detector.current.poll(Date.now()) !== 'blocked') return
      onBlocked?.(message(planRef.current ?? noCompositePlan()))
    }, 1000)
    return () => clearInterval(poll)
  }, [onBlocked, message, started])

  useEffect(() => () => retractDebug('__OGS_RENDER_PLAN__'), [])

  useFrame(() => {
    // Two reference compares on the idle path — the editor replaces both arrays on an edit.
    if (faceGraphState.nodes === nodesRef.current && faceGraphState.edges === edgesRef.current) return
    nodesRef.current = faceGraphState.nodes
    edgesRef.current = faceGraphState.edges
    const next = resolveRenderPlan(faceGraphState.nodes, faceGraphState.edges)
    const prev = planRef.current
    planRef.current = next
    publish(next)
    if (!started()) {
      const change = detector.current.notePhase(next.phase, Date.now())
      if (change === 'unblocked') onBlocked?.(null)
    }
    onPlan(next, prev)
  })
}
